/*
 * Copyright 2026 Sophos Limited. All rights reserved.
 *
 * 'Sophos' and 'Sophos Anti-Virus' are registered trademarks of Sophos Limited and Sophos Group. All other product
 * and company names mentioned are trademarks or registered trademarks of their respective owners.
 */
import { atom } from "jotai";
import { config } from "../config/AdminUiConfig";
import { handleError } from "./apiError";
import { accessTokenAtom } from "./userService";
import { loadableSet } from "../helpers/loadableSetter";

export type DLQMessageSummary = {
  /** Unique identifier for the message */
  messageId: string;

  /** ISO timestamp of the message */
  timestamp: string;

  /** Brief preview of message content */
  snippet: string;

  /** Raw message body as string */
  rawBody: string;
};

export type DLQItem = {
  /** Logical queue name */
  queueName: string;

  /** Physical AWS queue name */
  queuePhysicalName?: string;

  /** AWS region where queue resides */
  region: string;

  /** Number of messages in DLQ */
  count: number;

  /** Individual messages from the DLQ */
  messages: DLQMessageSummary[];
};

export type DLQResult = {
  /** All DLQs items found */
  items: DLQItem[];

  /** Total number of messages across all DLQs */
  totalCount: number;

  /** When this data was fetched */
  fetchedAt: string;
};

export type PipelineError = {
  /** Error message text */
  message: string;

  /** AWS region where error occurred */
  region: string;

  /** Number of times this error occurred */
  count: number;

  /** Organisation ID if consistent across all errors */
  organisationId?: string;

  /** True if error spans multiple organisations */
  organisationIdVarious?: boolean;

  /** Alert ID if consistent across all errors */
  alertId?: string;

  /** True if error spans multiple alert IDs */
  alertIdVarious?: boolean;
};

export type ErrorsResult = {
  /** Pipeline errors from the last 24 hours */
  errors: PipelineError[];

  /** When this data was fetched */
  fetchedAt: string;
};

export type CloudSourceHealth = {
  /** Cloud source name (e.g. AWS, Azure, GCP) */
  name: string;

  /** Total number of organisations using this source */
  totalOrgs: number;

  /** Number of organisations with errors */
  errorOrgs: number;

  /** Percentage of organisations experiencing errors */
  errorPercentage: number;

  /** Additional details about the health status */
  details: string;
};

export type CloudSourcesResult = {
  /** Health metrics for all cloud sources */
  sources: CloudSourceHealth[];

  /** When this data was fetched */
  fetchedAt: string;
};

export type FilterStat = {
  /** Organisation ID hitting the filter rate limit */
  organisationId: string;

  /** Number of events in this organisation */
  eventCount: number;

  /** AWS region where events occurred */
  region: string;
};

export type FilterRule = {
  /** Filter rule identifier */
  rule: string;

  /** Number of events matching this rule */
  count: number;
};

export type FilterStatsResult = {
  /** Top organisations hitting filter rate limits */
  topOrgs: FilterStat[];

  /** Filter rules with their event counts */
  rules: FilterRule[];

  /** When this data was fetched */
  fetchedAt: string;
};

export type DailyChecksData = {
  /** Dead letter queue data, or null if fetch failed */
  dlq: DLQResult | null;

  /** Pipeline errors data, or null if fetch failed */
  errors: ErrorsResult | null;

  /** Cloud source health data, or null if fetch failed */
  cloudSources: CloudSourcesResult | null;

  /** Filter statistics data, or null if fetch failed */
  filterStats: FilterStatsResult | null;

  /** When the checks were executed */
  checkedAt: string;
};

/**
 * State to store the most recently fetched daily checks data
 */
export const dailyChecksDataAtom = atom<DailyChecksData | null>(null);

/**
 * Setter atom that fetches the daily checks summary from the Admin API.
 * The Admin API endpoint proxies DLQ, pipeline error, cloud source, and filter
 * stats data — including Logz.io queries performed server-side.
 */
export const fetchDailyChecksState = atom(null, async (get, set) => {
  const accessToken = get(accessTokenAtom);
  try {
    const response = await fetch(`${config.apiUri}/dailyChecks`, {
      headers: { Authorization: accessToken },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const data: DailyChecksData = await response.json();
    set(dailyChecksDataAtom, data);
  } catch (error: unknown) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    handleError(error as Error, loadableFetchDailyChecksState);
    throw error;
  }
});

/** Loadable setter for fetchDailyChecksState */
export const loadableFetchDailyChecksState = loadableSet(fetchDailyChecksState);
